/**
 * Price arithmetic for the shop. Everything is in integer euro cents, VAT included (prices
 * are shown to consumers gross). No floats are ever produced: rounding is done with integer
 * division, so sums and VAT breakdowns are exact and reproducible.
 */

export type RigaOrdine = {
  /** Unit price in cents, VAT included. */
  prezzoCentesimi: number
  quantita: number
  /** VAT rate in percent, e.g. 22. */
  aliquotaIva: number
}

export type ScorporoIva = {
  lordo: number
  imponibile: number
  iva: number
}

export type RiepilogoAliquota = ScorporoIva & { aliquotaIva: number }

export type TotaleOrdine = ScorporoIva & {
  /** One entry per VAT rate, sorted by rate. */
  riepilogoIva: RiepilogoAliquota[]
}

const assertCents = (value: number, name: string): void => {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(
      `${name} deve essere un intero non negativo (centesimi), ricevuto ${value}`,
    )
  }
}

const assertAliquota = (aliquota: number): void => {
  if (!Number.isSafeInteger(aliquota) || aliquota < 0 || aliquota > 100) {
    throw new RangeError(`Aliquota IVA non valida: ${aliquota}`)
  }
}

/** Integer division rounded half-up. Both arguments must be non-negative integers. */
const divRound = (numerator: number, denominator: number): number =>
  Math.floor((2 * numerator + denominator) / (2 * denominator))

const euroFormatter = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })

/**
 * `1234` → `"12,34 €"`. The amount is passed to `Intl` as a decimal string, so it never goes
 * through a float.
 */
export const formatEuro = (cents: number): string =>
  // `format()` accepts decimal strings (Intl.NumberFormat v3); the DOM typings lag behind.
  euroFormatter.format(prezzoDecimale(cents) as unknown as number)

/** `1234` → `"12.34"`: the machine-readable form, e.g. for schema.org `Offer.price`. */
export const prezzoDecimale = (cents: number): string => {
  if (!Number.isSafeInteger(cents)) {
    throw new RangeError(`Servono centesimi interi, ricevuto ${cents}`)
  }

  const abs = Math.abs(cents)
  return `${cents < 0 ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

/**
 * Splits a VAT-included amount into taxable base and VAT. The base is rounded half-up to the
 * cent and the VAT is the remainder, so `imponibile + iva === lordo` always holds.
 */
export const scorporoIva = (lordo: number, aliquotaIva: number): ScorporoIva => {
  assertCents(lordo, 'lordo')
  assertAliquota(aliquotaIva)

  const imponibile = divRound(lordo * 100, 100 + aliquotaIva)

  return { lordo, imponibile, iva: lordo - imponibile }
}

/** Unit price × quantity, VAT included. */
export const totaleRiga = ({ prezzoCentesimi, quantita }: RigaOrdine): number => {
  assertCents(prezzoCentesimi, 'prezzoCentesimi')
  if (!Number.isSafeInteger(quantita) || quantita < 0) {
    throw new RangeError(`Quantità non valida: ${quantita}`)
  }

  const totale = prezzoCentesimi * quantita
  assertCents(totale, 'totale riga')
  return totale
}

/**
 * Order total with its VAT breakdown. VAT is extracted once per rate on the summed gross
 * amount (not per line), which keeps the rounding error to at most half a cent per rate.
 */
export const totaleOrdine = (righe: RigaOrdine[]): TotaleOrdine => {
  const lordoPerAliquota = new Map<number, number>()

  for (const riga of righe) {
    assertAliquota(riga.aliquotaIva)
    lordoPerAliquota.set(
      riga.aliquotaIva,
      (lordoPerAliquota.get(riga.aliquotaIva) ?? 0) + totaleRiga(riga),
    )
  }

  const riepilogoIva = [...lordoPerAliquota.entries()]
    .sort(([a], [b]) => a - b)
    .map(([aliquotaIva, lordo]) => ({ aliquotaIva, ...scorporoIva(lordo, aliquotaIva) }))

  return riepilogoIva.reduce<TotaleOrdine>(
    (totale, { lordo, imponibile, iva }) => ({
      ...totale,
      lordo: totale.lordo + lordo,
      imponibile: totale.imponibile + imponibile,
      iva: totale.iva + iva,
    }),
    { lordo: 0, imponibile: 0, iva: 0, riepilogoIva },
  )
}
